import React, { useContext, useMemo } from "react";
import { useHistory } from "react-router-dom";

import { PageLayout, PageHeader } from "../../components/layout";
import { LookbookLibrary } from "../../listings/components/lookbook/LookbookLibrary";
import { SettingsContext, UserContext } from "../../contexts";
import { useStoreWriteGuard } from "../../helpers/storeScope";
import { useOrdersTabs, ORDERS_ACTIONS, NEW_LOOKBOOK_ACTION } from "../../views/Orders/ordersTabs";
import { LOOKBOOK_QUERY_PARAM, NEW_LOOKBOOK_QUERY_PARAM } from "./useRequestLookbook";
import "../../listings/pages/rc-lookbooks-page.scss";
import { HOME_PATH } from "../../core/config/dashboard.js";

/**
 * Orders → Lookbooks. Same library as Listings → Lookbooks (same component),
 * listing the request flavour instead.
 *
 * Both "+ New Lookbook" and "Edit" lead back to the Orders list, because the
 * Orders list — with its filters, its search and every request in it — IS the
 * picker. There is no separate builder screen to keep in step.
 */
const RequestLookbooksPage = () => {
  const history = useHistory();
  const settings = useContext(SettingsContext);
  const { user } = useContext(UserContext);
  // A lookbook is saved against one store; from "Master Store" there is no
  // single store to file it under. Same guard the Orders page applies.
  const storeGuard = useStoreWriteGuard().guardProps;
  const tabs = useOrdersTabs();

  const suffix = settings?.iframeQueryParams ? `&${settings.iframeQueryParams}` : "";

  const actions = useMemo(() => {
    const newLookbook = {
      ...NEW_LOOKBOOK_ACTION,
      ...storeGuard,
      onClick: () =>
        history.push(`/requests/orders?${NEW_LOOKBOOK_QUERY_PARAM}=1${suffix}`),
    };

    return user?.is_admin
      ? [...ORDERS_ACTIONS.map((a) => ({ ...a, ...storeGuard })), newLookbook]
      : [newLookbook];
  }, [history, storeGuard, suffix, user?.is_admin]);

  return (
    <PageLayout className="rc-lookbooks">
      <PageHeader
        title="Lookbooks"
        description="Create and manage PDF lookbooks built from your requests."
        backTo={HOME_PATH}
        tabs={tabs}
        actions={actions}
        activePath="/requests/lookbooks"
      />

      <LookbookLibrary
        type="request"
        getEditPath={(lb) =>
          `/requests/orders?${LOOKBOOK_QUERY_PARAM}=${lb.id}${suffix}`
        }
        emptyDescription="Pick requests from the Orders list to build your first request lookbook."
      />
    </PageLayout>
  );
};

export default RequestLookbooksPage;
